"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useGetPuck } from "@puckeditor/core";
import { Button } from "@astryxdesign/core/Button";
import { Icon } from "@astryxdesign/core/Icon";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { researchIcons } from "@/lib/icons/research-icons";
import type { LandingPuckData } from "@/lib/landing/puck/types";

type LandingMediaUploadFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** image (по умолчанию) или pdf. */
  kind?: "image" | "pdf";
  folder?: string;
  onUploaded?: (url: string) => void;
  /** Подсказка после загрузки. Пустая строка — без текста. */
  uploadedNotice?: string;
};

/** Единая загрузка медиа в S3 — без ручного URL. */
export function LandingMediaUploadField({
  value,
  onChange,
  label = "Файл",
  kind = "image",
  folder,
  onUploaded,
  uploadedNotice = "Сохраняем черновик…",
}: LandingMediaUploadFieldProps) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(
    typeof value === "string" ? value : "",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPdf = kind === "pdf";

  useEffect(() => {
    setPreview(typeof value === "string" ? value : "");
  }, [value]);

  useEffect(() => {
    void fetch("/api/admin/landings/media", { cache: "no-store" })
      .then(async (r) => {
        const data = (await r.json().catch(() => null)) as {
          configured?: boolean;
          error?: string;
        } | null;
        // 403 без configured раньше выглядел как «S3 не настроен»
        if (r.status === 401 || r.status === 403) {
          setConfigured(null);
          setError("Нет доступа admin — войдите снова");
          return;
        }
        if (!r.ok) {
          setConfigured(false);
          setError(data?.error ?? "Не удалось проверить S3");
          return;
        }
        setConfigured(Boolean(data?.configured));
      })
      .catch(() => {
        setConfigured(false);
        setError("Не удалось проверить S3");
      });
  }, []);

  const upload = useCallback(
    async (file: File) => {
      if (isPdf) {
        const ok =
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf");
        if (!ok) {
          setError("Нужен файл PDF");
          return;
        }
      } else if (!file.type.startsWith("image/")) {
        setError("Нужен файл изображения (JPEG, PNG, WebP…)");
        return;
      }
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("kind", kind);
        if (folder) form.append("folder", folder);
        const res = await fetch("/api/admin/landings/media", {
          method: "POST",
          body: form,
        });
        const payload = (await res.json().catch(() => null)) as {
          error?: string;
          url?: string;
          serveUrl?: string;
        } | null;
        if (!res.ok) {
          throw new Error(payload?.error ?? "Не удалось загрузить");
        }
        const nextUrl =
          (isPdf ? payload?.serveUrl ?? payload?.url : payload?.url) ?? "";
        if (nextUrl) {
          setPreview(nextUrl);
          onChange(nextUrl);
          onUploaded?.(nextUrl);
          setNotice(uploadedNotice.trim() ? uploadedNotice : null);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Ошибка загрузки");
      } finally {
        setBusy(false);
      }
    },
    [folder, isPdf, kind, onChange, onUploaded, uploadedNotice],
  );

  const disabled = busy || configured !== true;

  return (
    <VStack gap={2} align="start" width="100%">
      <Text size="sm">{label}</Text>
      {configured === false ? (
        <Text size="sm" color="secondary">
          S3 не настроен (S3_ENDPOINT, ключи, бакет, S3_PUBLIC_BASE_URL в
          .env.dev / .env.prod). Загрузка недоступна.
        </Text>
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files?.[0];
          if (file) void upload(file);
        }}
        className={[
          "relative w-full overflow-hidden rounded-md border border-dashed text-left transition-colors",
          dragOver
            ? "border-accent bg-muted"
            : "border-border bg-canvas hover:bg-muted/60",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        ].join(" ")}
      >
        {preview && !isPdf ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={preview}
            src={preview}
            alt=""
            className="max-h-36 w-full object-cover"
          />
        ) : preview && isPdf ? (
          <div className="flex flex-col items-center gap-2 px-3 py-6">
            <Icon icon={researchIcons.fileText} size="md" color="secondary" />
            <Text size="sm" color="secondary" className="text-center">
              PDF загружен
            </Text>
            <span className="text-xs text-primary underline">{preview}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 px-3 py-6">
            <Icon
              icon={isPdf ? researchIcons.fileText : researchIcons.imagePlus}
              size="md"
              color="secondary"
            />
            <Text size="sm" color="secondary" className="text-center">
              {busy
                ? "Загрузка…"
                : isPdf
                  ? "Перетащите PDF сюда или нажмите, чтобы выбрать"
                  : "Перетащите картинку сюда или нажмите, чтобы выбрать"}
            </Text>
          </div>
        )}
      </button>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={
            isPdf
              ? "application/pdf,.pdf"
              : "image/jpeg,image/png,image/webp,image/avif,image/gif"
          }
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = "";
          }}
        />
        <Button
          size="sm"
          variant="secondary"
          label={busy ? "…" : "Загрузить в S3"}
          isDisabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        />
        {preview ? (
          <Button
            size="sm"
            variant="ghost"
            label="Убрать"
            isDisabled={busy}
            onClick={() => {
              setPreview("");
              onChange("");
            }}
          />
        ) : null}
        {preview && isPdf ? (
          <a
            href={preview}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-md px-2 text-sm text-primary underline"
          >
            Открыть
          </a>
        ) : null}
      </div>
      {notice ? (
        <Text size="sm" color="secondary">
          {notice}
        </Text>
      ) : null}
      {error ? (
        <Text size="sm" color="secondary">
          {error}
        </Text>
      ) : null}
    </VStack>
  );
}

/** @deprecated alias — только S3, без ручного URL. */
export function LandingBackgroundImageField(
  props: Omit<LandingMediaUploadFieldProps, "kind">,
) {
  return <LandingMediaUploadField {...props} kind="image" />;
}

function dispatchSaveDraftFromPuck(getPuck: () => { appState: { data: unknown } }) {
  window.setTimeout(() => {
    try {
      const data = getPuck().appState.data as LandingPuckData;
      window.dispatchEvent(
        new CustomEvent("landing:save-draft", { detail: { data } }),
      );
    } catch {
      window.dispatchEvent(new CustomEvent("landing:save-draft"));
    }
  }, 0);
}

/** Поле Puck для картинки — загрузка в S3. */
export function LandingBackgroundImagePuckField({
  value,
  onChange,
  label = "Фоновое изображение",
  folder,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  folder?: string;
}) {
  const getPuck = useGetPuck();
  return (
    <LandingMediaUploadField
      value={typeof value === "string" ? value : ""}
      onChange={onChange}
      label={label}
      kind="image"
      folder={folder}
      onUploaded={() => dispatchSaveDraftFromPuck(getPuck)}
    />
  );
}

/** Поле Puck для PDF — загрузка в S3. */
export function LandingPdfUploadPuckField({
  value,
  onChange,
  label = "PDF",
  folder = "cases",
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  folder?: string;
}) {
  const getPuck = useGetPuck();
  return (
    <LandingMediaUploadField
      value={typeof value === "string" ? value : ""}
      onChange={onChange}
      label={label}
      kind="pdf"
      folder={folder}
      onUploaded={() => dispatchSaveDraftFromPuck(getPuck)}
    />
  );
}
